import {createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable} from '@tanstack/react-table';
import {useMemo, useState} from 'react';
import SearchInput from 'src/autobahn/components/search-input';
import styles from './upcoming-tab.module.scss';
import sharedStyles from 'src/autobahn/shared/styles.module.scss';
import Table from 'src/autobahn/components/table';
import {useMaintenances, useMaintenancesRealizedByStatus} from './queries';
import {useTablePagination} from 'src/autobahn/hooks/useTablePagination';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import Button from 'src/autobahn/components/button/button';
import useTimezone from 'src/hooks/use-timezone';
import {formatDate} from 'src/helpers/date';
import {useIntl} from 'react-intl';
import {formatMeasure} from './format-measure';


type CompletedMaintenance = {
  id: number;
  vehicle: string; // {plate} - {model} e.g. "ABC-1234 - Corolla"
  title: string; // maintenance title
  kind: string; // e.g. "odometer, hourmeter, date"
  odometer?: number;
  hourmeter?: number;
  date?: string;
  cost: number;
}

const columnHelper = createColumnHelper<CompletedMaintenance>();

const MaintenanceCompletedTab = () => {
  const {data: maintenances} = useMaintenances();
  const {data: completedMaintenances} = useMaintenancesRealizedByStatus('completed');
  const [filterText, setFilterText] = useState('');

  const {timezone} = useTimezone();

  const intl = useIntl();

  const data = useMemo(() => completedMaintenances.reduce((accu, completedMaintenance) => {
    const currentMaintenancePlan = maintenances.find((maintenances) => maintenances.id === completedMaintenance.maintenanceId);

    if (!currentMaintenancePlan) {
      return accu;
    }

    accu.push({
      id: completedMaintenance.id as number,
      vehicle: completedMaintenance.vehicle!,
      title: currentMaintenancePlan.name,
      kind: currentMaintenancePlan.type,
      odometer: completedMaintenance.nextOdometer,
      hourmeter: completedMaintenance.nextHourmeter,
      date: completedMaintenance.date ? formatDate(completedMaintenance.date, intl.formatMessage({id: 'dateFormat'}), timezone) : undefined,
      cost: completedMaintenance.cost as unknown as number,
    });

    return accu;
  }, [] as CompletedMaintenance[]), [maintenances, completedMaintenances, timezone]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'vehicle',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedVehicle'})}</span>
            <span className={styles.rowDescription}>{row.original.vehicle}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'title',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedTitle'})}</span>
            <span className={styles.rowDescription}>{row.original.title}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'kind',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedKind'})}</span>
            {!!rowValue.odometer && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedOdometer'})}</span>}
            {!!rowValue.date && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedDate'})}</span>}
            {!!rowValue.hourmeter && <span className={styles.rowDescription}>{intl.formatMessage({id: 'sharedHourmeter'})}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'period',
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedNextF'})}</span>
            {!!rowValue.odometer && <span className={styles.rowDescription}>{formatMeasure(rowValue.odometer)}</span>}
            {!!rowValue.date && <span className={styles.rowDescription}>{rowValue.date}</span>}
            {!!rowValue.hourmeter && <span className={styles.rowDescription}>{formatMeasure(rowValue.hourmeter)}</span>}
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'cost',
      cell: ({row}) => {
        return (
          <div className={styles.kindAndPeriodCell}>
            <span className={styles.rowTitle}>{intl.formatMessage({id: 'sharedCost'})}</span>
            <span className={styles.rowDescription}>{row.original.cost}</span>
          </div>
        );
      },
      meta: {
        className: sharedStyles.verticalAlignBaseline,
      },
    }),
  ], []);

  const {pagination, setPagination, currentPageSize} = useTablePagination();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilterText,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: 'includesString',
    state: {
      pagination: {
        ...pagination,
        pageSize: currentPageSize,
      },
      globalFilter: filterText,
    },
  });

  return (
    <div className={styles.container}>
      {/* styles.actionBar */}
      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
        <SearchInput onChange={table.setGlobalFilter} />
      </div>

      <Table table={table} hideHeaders emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})} />

      <TablePageControls
        pageCount={table.getPageCount()}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        onPageSizeChange={(newPageSize) => {
          if (newPageSize === -1) {
            table.setPageSize(data.length);
            setPagination((prev) => ({...prev, pageSize: -1}));
          } else {
            table.setPageSize(newPageSize);
            setPagination((prev) => ({...prev, pageSize: newPageSize}));
          }
        }}
        setPageIndex={table.setPageIndex}
        getCanPreviousPage={table.getCanPreviousPage}
        getCanNextPage={table.getCanNextPage}
        previousPage={table.previousPage}
        nextPage={table.nextPage}
      />
    </div>
  );
};

export default MaintenanceCompletedTab;
